import {
    ButtonGradientPrimary,
    Colors,
    ConfirmDialog,
    Icon,
    Modal as AmelioModal,
    Text,
} from '@amelio/ui-library';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import {
    buildCreateProfileWizardSearch,
    buildCreateProfileWizardLocationState,
    CreateProfileReturnMode,
} from '../../createProfileWizardNavigation';
import { ActionMenu, type ActionMenuItem } from '../ActionMenu';
import {
    ChevronDownIcon,
    ChevronUpIcon,
    EyeIcon,
    PencilIcon,
    PlusIcon,
    PowerIcon,
    Trash2Icon,
    UserPlusIcon,
} from '../icons';
import { AssignProfileWizard } from './AssignProfileWizard';
import { CompetencyAddModal } from './CompetencyAddModal';
import { DeleteCompetencyProfileModal } from './DeleteCompetencyProfileModal';
import { ProfileAssignedEmployeesModal } from './ProfileAssignedEmployeesModal';
import { ProfileForm } from './ProfileForm';

import type { ProfileFormData } from '../../validation';
import type { CompetencyGroup } from '@/types/competency/Competency';
import type {
    CompetencyProfile,
    CompetencyProfileItem,
} from '@/types/competency/CompetencyProfile';

import {
    useGetCompetencyProfiles,
    useSetCompetencyProfileStatus,
    useUpdateCompetencyProfile,
} from '@/api/competency-services/competency-profile';
import { useGetProfileAssignmentStatistics } from '@/api/competency-services/profile-assignment';
import profileAssignmentService from '@/api/competency-services/profile-assignment/profileAssignmentService';
import { Permission } from '@/constants/Permission';
import { ProficiencyLevel } from '@/constants/ProficiencyLevel';
import { ProfileAssignmentQueryKey } from '@/constants/QueryKeys';
import { useAmelioTranslations } from '@/hooks';
import { useUserPermissions } from '@/hooks/api/useUserPermissions';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { useError } from '@/hooks/utils/useError';
import { useNavigate } from '@/hooks/utils/useNavigate';
import { RouteName } from '@/navigation/types';
import { showSuccessToast } from '@/plugins/Toast';
import { CompetencyStatus } from '@/types/competency/Competency';
import { getTextByLanguage } from '@/utils/localization';

type CompetencyProfilesTabProps = {
    competencyGroups: CompetencyGroup[];
};

type CompetencyInfo = {
    name: string;
    description: string;
    groupId: string;
    groupName: string;
    isActive: boolean;
};

/**
 * Builds a localized record by setting the value only for the active language.
 * No base language is auto-filled; each value is stored under the active UI language.
 */
const buildLocalizedRecord = (
    existing: Record<string, string> | undefined,
    language: string,
    value: string,
): Record<string, string> => {
    const result: Record<string, string> = { ...(existing ?? {}) };
    result[language] = value;

    return result;
};

export const CompetencyProfilesTab = ({ competencyGroups }: CompetencyProfilesTabProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();
    const { handleError } = useError();

    const { data: profiles, isFetching, isError, refetch } = useGetCompetencyProfiles();
    const navigate = useNavigate();
    const { mutateAsync: updateProfile, isPending: isUpdating } = useUpdateCompetencyProfile();
    const { mutateAsync: setProfileStatus, isPending: isSettingStatus } =
        useSetCompetencyProfileStatus();

    const [openProfileIds, setOpenProfileIds] = useState<Set<string>>(() => new Set());
    const [profileToEdit, setProfileToEdit] = useState<CompetencyProfile | null>(null);
    const [profileToDelete, setProfileToDelete] = useState<CompetencyProfile | null>(null);
    const [profileToDeactivate, setProfileToDeactivate] = useState<CompetencyProfile | null>(null);
    const [profileToAddTo, setProfileToAddTo] = useState<CompetencyProfile | null>(null);
    const [profileToAssign, setProfileToAssign] = useState<CompetencyProfile | null>(null);
    const [profileForAssignedEmployees, setProfileForAssignedEmployees] =
        useState<CompetencyProfile | null>(null);
    const [itemToRemove, setItemToRemove] = useState<{
        profile: CompetencyProfile;
        competencyId: string;
    } | null>(null);

    const { data: removeImpactAssignments } = useQuery({
        queryKey: [ProfileAssignmentQueryKey.ASSIGNMENTS, itemToRemove?.profile.id],
        queryFn: () => profileAssignmentService.getAssignments(itemToRemove!.profile.id),
        enabled: Boolean(itemToRemove),
    });
    const removeImpactEmployeeCount = removeImpactAssignments?.length ?? 0;

    const { data: assignmentStatistics } = useGetProfileAssignmentStatistics();
    const { can } = useUserPermissions();
    const canAssignCompetencyProfiles = can(Permission.AssignCompetencyProfiles);
    // Viewing who a profile is assigned to reads employee data, so it needs the
    // performance-profile permission on top of competency administration (#14504).
    const canViewAssignedEmployees = can(
        Permission.ViewPerformanceProfile,
        Permission.ManageCompetencies,
    );

    const assignedCountByProfileId = useMemo(
        () =>
            new Map(
                (assignmentStatistics ?? []).map((statistic) => [
                    statistic.profileId,
                    statistic.count,
                ]),
            ),
        [assignmentStatistics],
    );

    const competencyInfoById = useMemo(() => {
        const map = new Map<string, CompetencyInfo>();
        competencyGroups.forEach((group) => {
            const groupName = getTextByLanguage(group.name, language);
            group.competencies.forEach((competency) => {
                map.set(competency.id, {
                    name: getTextByLanguage(competency.name, language),
                    description: getTextByLanguage(competency.description, language),
                    groupId: group.id,
                    groupName,
                    isActive: competency.status === CompetencyStatus.ACTIVE,
                });
            });
        });

        return map;
    }, [competencyGroups, language]);

    const profileList = profiles ?? [];

    const persistItems = async (profile: CompetencyProfile, items: CompetencyProfileItem[]) => {
        try {
            await updateProfile({
                id: profile.id,
                payload: {
                    name: profile.name,
                    description: profile.description,
                    sortOrder: profile.sortOrder,
                    items,
                },
            });
            showSuccessToast({ message: t('competencies.profiles.toast.updated') });
        } catch (error) {
            handleError({ error });
        }
    };

    const onEditProfile = async (data: ProfileFormData) => {
        if (!profileToEdit) {
            return;
        }

        try {
            await updateProfile({
                id: profileToEdit.id,
                payload: {
                    name: buildLocalizedRecord(profileToEdit.name, language, data.name),
                    description: buildLocalizedRecord(
                        profileToEdit.description,
                        language,
                        data.description,
                    ),
                    sortOrder: profileToEdit.sortOrder,
                    items: profileToEdit.items,
                },
            });
            showSuccessToast({ message: t('competencies.profiles.toast.updated') });
            setProfileToEdit(null);
        } catch (error) {
            handleError({ error });
        }
    };

    const onAddCompetencies = async (competencyIds: string[]) => {
        if (!profileToAddTo) {
            return;
        }

        const existingIds = new Set(profileToAddTo.items.map((item) => item.competencyId));
        const newItems: CompetencyProfileItem[] = competencyIds
            .filter((id) => !existingIds.has(id))
            .map((id) => ({
                competencyId: id,
                expectedLevel: ProficiencyLevel.SATISFACTORY,
                isPriority: false,
            }));

        if (newItems.length > 0) {
            await persistItems(profileToAddTo, [...profileToAddTo.items, ...newItems]);
        }

        setProfileToAddTo(null);
    };

    const onTogglePriority = (profile: CompetencyProfile, competencyId: string) => {
        void persistItems(
            profile,
            profile.items.map((item) =>
                item.competencyId === competencyId
                    ? { ...item, isPriority: !item.isPriority }
                    : item,
            ),
        );
    };

    const confirmRemoveCompetency = () => {
        if (!itemToRemove) {
            return;
        }

        void persistItems(
            itemToRemove.profile,
            itemToRemove.profile.items.filter(
                (item) => item.competencyId !== itemToRemove.competencyId,
            ),
        );
        setItemToRemove(null);
    };

    const onToggleStatus = async (profile: CompetencyProfile, isActive: boolean) => {
        try {
            await setProfileStatus({ id: profile.id, isActive });
            showSuccessToast({
                message: isActive
                    ? t('competencies.profiles.toast.activated')
                    : t('competencies.profiles.toast.deactivated'),
            });
            setProfileToDeactivate(null);
        } catch (error) {
            handleError({ error });
        }
    };

    const confirmDeactivateProfile = async () => {
        if (!profileToDeactivate) {
            return;
        }

        await onToggleStatus(profileToDeactivate, false);
    };

    const profileActions = (profile: CompetencyProfile): ActionMenuItem[] => [
        ...(canAssignCompetencyProfiles
            ? [
                  {
                      id: 'assign',
                      label: t('competencies.profiles.menu.assign'),
                      icon: <UserPlusIcon className={'h-4 w-4'} />,
                      onClick: () => setProfileToAssign(profile),
                  },
              ]
            : []),
        ...(canViewAssignedEmployees
            ? [
                  {
                      id: 'view-assigned-employees',
                      label: t('competencies.profiles.menu.viewAssignedEmployees'),
                      icon: <EyeIcon className={'h-4 w-4'} />,
                      onClick: () => setProfileForAssignedEmployees(profile),
                  },
              ]
            : []),
        {
            id: 'edit',
            label: t('competencies.profiles.menu.edit'),
            icon: <PencilIcon className={'h-4 w-4'} />,
            onClick: () => setProfileToEdit(profile),
        },
        {
            id: 'toggle-status',
            label: profile.isActive
                ? t('competencies.profiles.menu.deactivate')
                : t('competencies.profiles.menu.activate'),
            icon: <PowerIcon className={'h-4 w-4'} />,
            onClick: () =>
                profile.isActive
                    ? setProfileToDeactivate(profile)
                    : void onToggleStatus(profile, true),
        },
        {
            id: 'delete',
            label: t('competencies.profiles.menu.delete'),
            icon: <Trash2Icon className={'h-4 w-4'} />,
            danger: true,
            onClick: () => setProfileToDelete(profile),
        },
    ];

    const hasProfiles = profileList.length > 0;

    return (
        <div
            className={
                'rounded-2xl border border-white bg-white/60 p-5 backdrop-blur-md sm:p-10 md:p-11'
            }
        >
            <div
                className={
                    'mb-8 flex flex-col items-start justify-start gap-4 sm:flex-row sm:items-center sm:justify-between'
                }
            >
                <h2 className={'text-navy text-subtitle font-medium'}>
                    {t('competencies.profiles.title')}
                </h2>

                <ButtonGradientPrimary
                    size={'md'}
                    onClick={() => {
                        const wizardState = buildCreateProfileWizardLocationState({
                            returnMode: CreateProfileReturnMode.CompetenciesTab,
                        });
                        navigate({
                            path: `${RouteName.CompetencyProfileCreate}${buildCreateProfileWizardSearch(wizardState)}`,
                            state: wizardState,
                        });
                    }}
                    leftIcon={<PlusIcon className={'h-5 w-5'} />}
                    dataTestId={'profile-create-button'}
                >
                    {t('competencies.profiles.create')}
                </ButtonGradientPrimary>
            </div>

            {isError && (
                <div className={'flex flex-col items-center justify-center py-6 text-center'}>
                    <p className={'text-error text-sm'}>{t('competencies.profiles.loadError')}</p>

                    <button
                        type={'button'}
                        onClick={() => void refetch()}
                        className={
                            'text-navy mt-4 flex h-11 items-center rounded-lg border border-gray-200 bg-white px-4 text-sm font-medium transition-colors hover:bg-[#f5f7f9]'
                        }
                        data-testid={'profiles-retry'}
                    >
                        {t('competencies.retry')}
                    </button>
                </div>
            )}

            {!isError && (isFetching || !hasProfiles) && (
                <p className={'py-6 text-center text-sm text-gray-600'}>
                    {t('competencies.profiles.empty')}
                </p>
            )}

            {!isError && hasProfiles && (
                <div className={'cmp-stack-4'}>
                    {profileList.map((profile) => (
                        <ProfileAccordionItem
                            key={profile.id}
                            profile={profile}
                            assignedEmployeesCount={assignedCountByProfileId.get(profile.id) ?? 0}
                            competencyInfoById={competencyInfoById}
                            isOpen={openProfileIds.has(profile.id)}
                            onToggle={() =>
                                setOpenProfileIds((current) => {
                                    const next = new Set(current);
                                    if (next.has(profile.id)) {
                                        next.delete(profile.id);
                                    } else {
                                        next.add(profile.id);
                                    }

                                    return next;
                                })
                            }
                            actions={profileActions(profile)}
                            onAddCompetency={() => setProfileToAddTo(profile)}
                            onRemoveCompetency={(competencyId) =>
                                setItemToRemove({ profile, competencyId })
                            }
                            onTogglePriority={(competencyId) =>
                                onTogglePriority(profile, competencyId)
                            }
                        />
                    ))}
                </div>
            )}

            <AmelioModal
                isOpen={Boolean(profileToEdit)}
                onClose={() => setProfileToEdit(null)}
                className={'w-130 max-w-[92vw]'}
                dataTestId={'profile-edit-modal'}
                zIndex={50}
            >
                {profileToEdit && (
                    <ProfileForm
                        mode={'edit'}
                        defaultValues={{
                            name: getTextByLanguage(profileToEdit.name, language),
                            description: getTextByLanguage(profileToEdit.description, language),
                        }}
                        isSubmitting={isUpdating}
                        onSubmit={onEditProfile}
                        onCancel={() => setProfileToEdit(null)}
                    />
                )}
            </AmelioModal>

            {profileToAddTo && (
                <CompetencyAddModal
                    groups={competencyGroups}
                    lockedCompetencyIds={profileToAddTo.items.map((item) => item.competencyId)}
                    selectedCompetencyIds={[]}
                    onConfirm={(ids) => void onAddCompetencies(ids)}
                    onCancel={() => setProfileToAddTo(null)}
                    profileName={profileToAddTo.name}
                />
            )}

            <AmelioModal
                isOpen={Boolean(profileToAssign)}
                onClose={() => setProfileToAssign(null)}
                className={'w-[896px] max-w-[92vw]'}
                dataTestId={'profile-assign-modal'}
                zIndex={50}
            >
                {profileToAssign && (
                    <AssignProfileWizard
                        profile={profileToAssign}
                        onClose={() => setProfileToAssign(null)}
                    />
                )}
            </AmelioModal>

            {profileForAssignedEmployees && (
                <ProfileAssignedEmployeesModal
                    profile={profileForAssignedEmployees}
                    onClose={() => setProfileForAssignedEmployees(null)}
                />
            )}

            {profileToDelete && (
                <AmelioModal
                    isOpen={true}
                    onClose={() => setProfileToDelete(null)}
                    className={'w-170 max-w-[92vw] scale-100 transition-none duration-0'}
                    dataTestId={'profile-delete-modal'}
                    zIndex={50}
                >
                    <DeleteCompetencyProfileModal
                        profile={profileToDelete}
                        allProfiles={profileList}
                        onCancel={() => setProfileToDelete(null)}
                    />
                </AmelioModal>
            )}

            <ConfirmDialog
                isOpen={Boolean(profileToDeactivate)}
                title={t('competencies.profiles.deactivateModal.title')}
                message={
                    profileToDeactivate && (
                        <div className={'flex flex-col gap-4'}>
                            <Text>
                                {t('competencies.profiles.deactivateModal.message', {
                                    profile: getTextByLanguage(profileToDeactivate.name, language),
                                })}
                            </Text>

                            <ImpactNotice
                                title={t('competencies.profiles.deactivateModal.impactTitle')}
                                body={t('competencies.profiles.deactivateModal.impactBody')}
                            />
                        </div>
                    )
                }
                confirmLabel={t('competencies.profiles.deactivateModal.confirm')}
                cancelLabel={t('common.cancel')}
                onConfirm={confirmDeactivateProfile}
                onCancel={() => setProfileToDeactivate(null)}
                isLoading={isSettingStatus}
                isDestructive
                dataTestId={'profile-deactivate'}
            />

            <ConfirmDialog
                isOpen={Boolean(itemToRemove)}
                title={t('competencies.profiles.removeModal.title')}
                message={
                    itemToRemove && (
                        <div className={'flex flex-col gap-4'}>
                            <Text>
                                {t('competencies.profiles.removeModal.message', {
                                    competency:
                                        competencyInfoById.get(itemToRemove.competencyId)?.name ??
                                        itemToRemove.competencyId,
                                    profile: getTextByLanguage(itemToRemove.profile.name, language),
                                })}
                            </Text>

                            <ImpactNotice
                                title={t('competencies.profiles.removeModal.impactTitle')}
                                body={t('competencies.profiles.removeModal.impactBody', {
                                    count: removeImpactEmployeeCount,
                                })}
                            />
                        </div>
                    )
                }
                confirmLabel={t('competencies.profiles.removeModal.confirm')}
                cancelLabel={t('common.cancel')}
                onConfirm={confirmRemoveCompetency}
                onCancel={() => setItemToRemove(null)}
                isLoading={isUpdating}
                isDestructive
                dataTestId={'profile-remove-competency'}
            />
        </div>
    );
};

type ImpactNoticeProps = {
    title: string;
    body: string;
};

const ImpactNotice = ({ title, body }: ImpactNoticeProps) => (
    <div className={'border-warning/50 bg-warning/10 flex flex-col gap-1 rounded-xl border p-4'}>
        <div className={'text-warning flex items-center gap-2'}>
            <Icon
                name={'essential-info-circle'}
                color={'inherit'}
                className={'h-4 w-4'}
            />

            <Text
                weight={'semibold'}
                color={'inherit'}
            >
                {title}
            </Text>
        </div>

        <Text
            size={'small'}
            color={Colors.BASE_600}
        >
            {body}
        </Text>
    </div>
);

type ProfileAccordionItemProps = {
    profile: CompetencyProfile;
    assignedEmployeesCount: number;
    competencyInfoById: Map<string, CompetencyInfo>;
    isOpen: boolean;
    onToggle: () => void;
    actions: ActionMenuItem[];
    onAddCompetency: () => void;
    onRemoveCompetency: (competencyId: string) => void;
    onTogglePriority: (competencyId: string) => void;
};

const ProfileAccordionItem = ({
    profile,
    assignedEmployeesCount,
    competencyInfoById,
    isOpen,
    onToggle,
    actions,
    onAddCompetency,
    onRemoveCompetency,
    onTogglePriority,
}: ProfileAccordionItemProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();

    const { groupedItems, activeItemCount } = useMemo(() => {
        const activeItems = profile.items.filter(
            (item) => competencyInfoById.get(item.competencyId)?.isActive,
        );
        const groups = new Map<string, { groupName: string; items: CompetencyProfileItem[] }>();

        activeItems.forEach((item) => {
            const info = competencyInfoById.get(item.competencyId);
            const groupId = info?.groupId ?? 'ungrouped';
            const groupName = info?.groupName ?? t('competencies.profiles.ungrouped');

            if (!groups.has(groupId)) {
                groups.set(groupId, { groupName, items: [] });
            }

            groups.get(groupId)!.items.push(item);
        });

        return { groupedItems: Array.from(groups.values()), activeItemCount: activeItems.length };
    }, [profile.items, competencyInfoById, t]);

    return (
        <div
            className={
                'rounded-xl border border-gray-200 bg-white/80 hover:!bg-white/70' +
                (!profile.isActive ? ' opacity-60' : '')
            }
        >
            <div className={'flex items-center gap-2 px-6 py-4'}>
                <button
                    type={'button'}
                    onClick={onToggle}
                    className={'flex flex-1 cursor-pointer items-center gap-3 text-left'}
                    data-testid={`profile-${profile.id}`}
                >
                    <div className={'flex min-w-0 flex-1 justify-between gap-2 max-sm:flex-col'}>
                        <div>
                            <div className={'flex items-center gap-2'}>
                                <h3
                                    className={
                                        'text-[18px] font-semibold ' +
                                        (!profile.isActive
                                            ? 'text-gray-600 line-through'
                                            : 'text-navy')
                                    }
                                >
                                    {getTextByLanguage(profile.name, language)}
                                </h3>

                                {!profile.isActive && (
                                    <span
                                        className={
                                            'rounded-full bg-gray-600/10 px-2 py-0.5 text-[10px] font-semibold tracking-wide text-gray-600 uppercase'
                                        }
                                    >
                                        {t('competencies.disabled')}
                                    </span>
                                )}
                            </div>

                            <p className={'mt-0.5 text-sm text-gray-600'}>
                                {getTextByLanguage(profile.description, language)}
                            </p>
                        </div>

                        <span
                            className={
                                'mt-1 flex flex-col items-start gap-2 sm:flex-row sm:items-center'
                            }
                        >
                            <span
                                className={
                                    'text-navy inline-block rounded-full bg-gray-200 px-3 py-1 text-xs font-medium whitespace-nowrap'
                                }
                                data-testid={`profile-employee-count-${profile.id}`}
                            >
                                {t('competencies.profiles.employeeCount', {
                                    count: assignedEmployeesCount,
                                })}
                            </span>
                            <span
                                className={
                                    'bg-primary/10 inline-block rounded-full px-3 py-1 text-xs font-medium whitespace-nowrap text-teal-500'
                                }
                            >
                                {t('competencies.profiles.competencyCount', {
                                    count: activeItemCount,
                                })}
                            </span>
                        </span>
                    </div>

                    <span className={'text-navy shrink-0'}>
                        {isOpen ? (
                            <ChevronUpIcon className={'h-6 w-6'} />
                        ) : (
                            <ChevronDownIcon className={'h-6 w-6'} />
                        )}
                    </span>
                </button>

                <ActionMenu
                    items={actions}
                    dataTestId={`profile-actions-${profile.id}`}
                />
            </div>

            {isOpen && (
                <div className={'border-t border-gray-200 px-6 py-6'}>
                    {groupedItems.map((group, index) => (
                        <div
                            key={index}
                            className={'mb-5 last:mb-0'}
                        >
                            <div className={'mb-3 flex items-center gap-2'}>
                                <div
                                    className={
                                        'bg-purple/10 text-purple flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[10px] font-bold'
                                    }
                                >
                                    {group.items.length}
                                </div>

                                <h4 className={'text-navy text-body font-semibold'}>
                                    {group.groupName}
                                </h4>

                                <div className={'ml-2 h-px flex-1 bg-gray-200/60'} />
                            </div>

                            <div className={'grid grid-cols-1 gap-2.5 pl-0 md:pl-1 lg:grid-cols-2'}>
                                {group.items.map((item) => {
                                    const info = competencyInfoById.get(item.competencyId);

                                    return (
                                        <div
                                            key={item.competencyId}
                                            className={
                                                'group/item hover:border-primary/30 flex items-center gap-3 rounded-2xl border border-gray-200/50 bg-white/80 px-4 py-3 transition-all hover:shadow-md'
                                            }
                                        >
                                            <div className={'min-w-0 flex-1'}>
                                                <h5
                                                    className={
                                                        'text-navy text-body truncate font-medium'
                                                    }
                                                >
                                                    {info?.name ?? item.competencyId}
                                                </h5>

                                                {info?.description && (
                                                    <p
                                                        className={
                                                            'mt-0.5 truncate text-[12px] text-gray-600'
                                                        }
                                                    >
                                                        {info.description}
                                                    </p>
                                                )}
                                            </div>

                                            <div className={'flex shrink-0 items-center gap-1.5'}>
                                                <button
                                                    type={'button'}
                                                    onClick={() =>
                                                        onTogglePriority(item.competencyId)
                                                    }
                                                    className={
                                                        'flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium transition-all ' +
                                                        (item.isPriority
                                                            ? 'bg-primary text-white'
                                                            : 'hover:bg-primary/10 hover:text-primary bg-gray-100 text-gray-600 opacity-0 group-hover/item:opacity-100')
                                                    }
                                                    title={
                                                        item.isPriority
                                                            ? t('competencies.profiles.priority')
                                                            : t(
                                                                  'competencies.profiles.wizard.markPriority',
                                                              )
                                                    }
                                                    data-testid={`profile-priority-${profile.id}-${item.competencyId}`}
                                                >
                                                    <svg
                                                        className={'h-3 w-3'}
                                                        fill={
                                                            item.isPriority
                                                                ? 'currentColor'
                                                                : 'none'
                                                        }
                                                        stroke={'currentColor'}
                                                        viewBox={'0 0 24 24'}
                                                    >
                                                        <path
                                                            strokeLinecap={'round'}
                                                            strokeLinejoin={'round'}
                                                            strokeWidth={2}
                                                            d={
                                                                'M11.48 3.5l2.2 4.46 4.92.72-3.56 3.47.84 4.9-4.4-2.31-4.4 2.31.84-4.9L4.4 8.68l4.92-.72 2.16-4.46z'
                                                            }
                                                        />
                                                    </svg>

                                                    {item.isPriority
                                                        ? t('competencies.profiles.priority')
                                                        : t(
                                                              'competencies.profiles.wizard.markPriority',
                                                          )}
                                                </button>

                                                <button
                                                    type={'button'}
                                                    onClick={() =>
                                                        onRemoveCompetency(item.competencyId)
                                                    }
                                                    className={
                                                        'hover:bg-error/10 hover:text-error rounded-md p-1 text-gray-600 opacity-0 transition-all group-hover/item:opacity-100'
                                                    }
                                                    title={t('competencies.profiles.remove')}
                                                    data-testid={`profile-remove-${profile.id}-${item.competencyId}`}
                                                >
                                                    <Trash2Icon
                                                        className={
                                                            'text-muted-foreground/40 h-4 w-4 hover:text-red-500'
                                                        }
                                                    />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}

                    <button
                        type={'button'}
                        onClick={onAddCompetency}
                        className={
                            'hover:border-primary hover:bg-primary/5 text-md mt-4 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#dbe0e6]/60 px-4 py-3 font-medium text-gray-600 transition-all hover:text-teal-500'
                        }
                        data-testid={`profile-add-competency-${profile.id}`}
                    >
                        <PlusIcon className={'h-4 w-4'} />

                        {t('competencies.profiles.addCompetency')}
                    </button>
                </div>
            )}
        </div>
    );
};
